import React, { useEffect } from "react";
import { MonitorX, CircleAlert } from "lucide-react";
import { VIDEO_CALL_END_REASONS } from "@/constants/video-call";

interface ExternalMonitorWarningProps {
  show: boolean;
  screenCount: number;
  previousScreenCount?: number;
  onClose?: () => void;
  violationCount?: number;
  roomDisconnected?: boolean;
  callId?: string;
  organizationId?: string;
}

export const ExternalMonitorWarning: React.FC<ExternalMonitorWarningProps> = ({
  show,
  screenCount,
  previousScreenCount,
  onClose,
  violationCount = 1,
  roomDisconnected = false,
  callId,
  organizationId,
}) => {
  // Debug logging
  useEffect(() => {
    console.log("ExternalMonitorWarning props:", { show, screenCount, previousScreenCount, violationCount, roomDisconnected });
  }, [show, screenCount, previousScreenCount, violationCount, roomDisconnected]);

  // Auto-navigate to interview complete after 3 seconds - but only after room disconnects
  useEffect(() => {
    if (!show || !roomDisconnected) {
      console.log("ExternalMonitorWarning: Waiting for room disconnect before navigation", { show, roomDisconnected });
      return;
    }

    console.log("ExternalMonitorWarning: Room disconnected, starting navigation timer");
    const timer = setTimeout(() => {
      console.log("ExternalMonitorWarning: Auto-navigating to interview complete after room disconnect");
      
      // Store violation data in session storage
      const violationData = {
        endReason: VIDEO_CALL_END_REASONS.MONITOR_VIOLATION,
        violationCount,
        violationType: 'external_monitor',
        screenCount,
        previousScreenCount,
        timestamp: new Date().toISOString(),
      };
      
      sessionStorage.setItem('interviewViolationData', JSON.stringify(violationData));
      
      // Navigate to interview complete page with call ID (no fallback generation)
      const effectiveCallId = callId || '';
      
      const params = new URLSearchParams({
        endReason: VIDEO_CALL_END_REASONS.MONITOR_VIOLATION,
        violations: violationCount.toString(),
        violationType: 'external_monitor',
        screenCount: screenCount.toString(),
        callId: effectiveCallId,
        organizationId: organizationId || '',
      });
      
      if (previousScreenCount) {
        params.append('previousScreenCount', previousScreenCount.toString());
      }
      
      window.location.replace(`/interview-complete?${params.toString()}`);
    }, 1000); // 1 second delay

    return () => clearTimeout(timer);
  }, [show, roomDisconnected, violationCount, screenCount, previousScreenCount, callId, organizationId]);

  if (!show) return null;

  const getDescription = () => {
    if (previousScreenCount && previousScreenCount < screenCount) {
      return `External monitor connected during interview (${screenCount} displays detected, was ${previousScreenCount}). This is a critical violation of proctoring rules.`;
    }
    return `Multiple displays detected (${screenCount} monitors). External monitors are not allowed during proctored interviews.`;
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="w-[480px] h-[338px] p-10 bg-[#131314] rounded-3xl border border-[#ec3f3f] flex flex-col justify-start items-start gap-2.5 overflow-hidden">
        <div className="self-stretch h-[258px] flex flex-col justify-center items-center gap-10">
          {/* Main content section */}
          <div className="self-stretch h-[180px] flex flex-col justify-start items-center gap-3">
            {/* Icon container with background circle */}
            <div className="w-[60px] h-[60px] relative flex justify-center items-center">
              <div className="w-[60px] h-[60px] absolute bg-[#ec4040]/20 rounded-full" />
              <MonitorX
                className="w-7 h-7 text-[#ec4040] z-10"
                strokeWidth={2}
                aria-label="External monitor detected icon"
              />
            </div>

            {/* Title and subtitle section */}
            <div className="self-stretch h-[52px] flex flex-col justify-start items-center gap-1">
              <h1 className="text-center text-white text-2xl font-bold font-satoshi leading-7">
                External Monitor Detected
              </h1>
              <h2 className="self-stretch text-center text-white text-base font-normal font-satoshi leading-tight">
                Critical Security Violation
              </h2>
            </div>

            {/* Description text */}
            <p className="self-stretch text-center text-white/70 text-base font-normal font-satoshi leading-normal">
              {getDescription()}
            </p>
          </div>

          {/* Alert */}
          <div className="px-4 py-2 bg-[#ec4040]/20 rounded flex justify-start items-center gap-2">
            <CircleAlert
              className="w-5 h-5 text-[#ec4040]"
              strokeWidth={2}
              aria-hidden="true"
            />
            <span className="text-center text-[#ec4040] text-base font-normal font-satoshi">
              Interview Ended
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}; 